import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { ChartConfiguration } from 'chart.js';
import { NGXLogger } from 'ngx-logger';
import { Subscription } from 'rxjs';
import FinancialDetailInfoViewModel from 'src/app/models/FinancialDetailInfoViewModel';
import { externalTooltipHandler } from 'src/app/shared/utils/customtooltip-utils';

import { BudgetCalculationService } from '../../budget/budget-calculation.service';
import { FetchEmployeesWithBudgets } from '../../budget/budget.actions';
import { BudgetState } from '../../budget/budget.state';
import Budget from '../../models/Budget';
import { Employee } from '../../models/Employee';
import EmployeeWithBudgets from '../../models/EmployeeWithBudgets';
import { MONTHS } from '../../models/MONTHS';
import { Timesheet } from '../../models/Timesheet';
import { SettingsState } from '../../settings/settings.state';
import { CalendarClickEventData, LegendPosition } from '../../shared/charts/default-line-chart/default-line-chart.component';
import { DateNavigationOptions, DateNavigationType, SelectedDateClass } from '../../shared/date-navigation/date-navigation.component';
import { roundNumber } from '../../shared/utils/number-utils';
import { capitalizeFirstLetter } from '../../shared/utils/string-utils';
import { FetchTimesheetsForEmployee } from '../../timesheets/timesheets.actions';
import { TimesheetState } from '../../timesheets/timesheets.state';
import { ChartInformationModalComponent, ChartInformationObjectType } from '../chart-information-modal/chart-information-modal.component';
import { FetchHourlyCostsForEmployee, FetchIncomeForEmployee } from '../financial.actions';
import { FinancialState } from '../financial.state';

@Component({
    selector: 'app-employee-graph',
    templateUrl: './employee-graph.component.html',
    styleUrls: ['./employee-graph.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EmployeeGraphComponent implements OnInit, OnDestroy {
    @Input() employee: Employee;

    data: any[] = undefined;
    labels: string[] = [];
    xLabels: string[];
    legendPosition: LegendPosition = LegendPosition.BOTTOM;
    options: ChartConfiguration['options'];

    incomeForEmployee: FinancialDetailInfoViewModel;
    costsForEmployee: FinancialDetailInfoViewModel;

    // Tooltip and hour computation helpers
    private projectHoursByMonth: Map<number, { project: string; hours: number }[]> = new Map();
    private timesheetsForEmployee: Timesheet[] = [];
    private employeesWithBudgets: EmployeeWithBudgets[] = [];
    private hourlyRateByMonth: number[] = new Array(12).fill(0);

    isLoading = false;
    selectedDate: Date = new Date();
    dateNavigationOptions: DateNavigationOptions;

    private subscriptions = new Subscription();

    constructor(public store: Store, private logger: NGXLogger, private modalService: NgbModal, private budgetCalc: BudgetCalculationService) {}

    ngOnInit(): void {
        this.dateNavigationOptions = {
            dateNavigationType: DateNavigationType.YEAR,
            selectedDate: this.selectedDate,
            selectedDateClass: SelectedDateClass.SMALL,
        };

        this.fetchData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    seedLabels() {
        return Object.values(MONTHS).map((label) => capitalizeFirstLetter(label.short));
    }

    private getSelectedYear(): number {
        return this.dateNavigationOptions?.selectedDate?.getFullYear() || new Date().getFullYear();
    }

    private getGraphCostData(): number[] {
        return this.costsForEmployee?.financialData || new Array(12).fill(0);
    }

    updateSelectedDate(): void {
        this.isLoading = true;
        let isIncomeLoading = true;
        let isCostsLoading = true;
        let isTimesheetsLoading = true;
        let isBudgetsLoading = true;
        this.subscriptions.add(
            this.store.dispatch(new FetchIncomeForEmployee(this.employee.uuid, this.dateNavigationOptions.selectedDate.getFullYear())).subscribe({
                next: () => {
                    isIncomeLoading = false;
                    if (!isCostsLoading && !isTimesheetsLoading && !isBudgetsLoading) this.isLoading = false;
                },
            }),
        );
        this.subscriptions.add(
            this.store.dispatch(new FetchHourlyCostsForEmployee(this.employee.uuid, this.dateNavigationOptions.selectedDate.getFullYear())).subscribe({
                next: () => {
                    isCostsLoading = false;
                    if (!isIncomeLoading && !isTimesheetsLoading && !isBudgetsLoading) this.isLoading = false;
                },
            }),
        );
        // Ensure budgets are loaded (used to compute hourly rate as in budgets overview)
        this.subscriptions.add(
            this.store.dispatch(new FetchEmployeesWithBudgets()).subscribe({
                next: () => {
                    isBudgetsLoading = false;
                    if (!isIncomeLoading && !isCostsLoading && !isTimesheetsLoading) this.isLoading = false;
                },
            }),
        );
        // Fetch timesheets for hours-per-project breakdown
        this.subscriptions.add(
            this.store.dispatch(new FetchTimesheetsForEmployee(this.employee.uuid)).subscribe({
                next: () => {
                    isTimesheetsLoading = false;
                    if (!isIncomeLoading && !isCostsLoading && !isBudgetsLoading) this.isLoading = false;
                },
            }),
        );
    }

    /**
     * Number of months (0-12) that should be summed for the selected year.
     * Mirrors the backend logic in `FinancialService.getYearToDateMonthCount`:
     *  - selected year != current year                                          -> 12
     *  - current year + setting "Include current month" ON                      -> 1..currentMonth
     *  - current year + setting OFF (default)                                   -> 1..currentMonth-1
     *    (in January this is 0)
     */
    private getMonthsToInclude(): number {
        const now = new Date();
        const selectedYear = this.dateNavigationOptions?.selectedDate?.getFullYear() || now.getFullYear();
        if (selectedYear !== now.getFullYear()) {
            return 12;
        }
        const includeCurrent = this.store.selectSnapshot(SettingsState.FinancialsIncludeCurrentMonth);
        const currentMonth = now.getMonth() + 1; // 1-based
        return includeCurrent ? currentMonth : Math.max(0, currentMonth - 1);
    }

    calculateTotalMarginForSelectedYearInPct(): number {
        const monthsToInclude = this.getMonthsToInclude();
        const totalIncome = this.incomeForEmployee.financialData.slice(0, monthsToInclude).reduce((t, n) => t + n, 0);

        const totalCost = this.getGraphCostData()
            .slice(0, monthsToInclude)
            .reduce((t, n) => t + n, 0);

        const margin = totalIncome - totalCost;
        if (margin === 0) return 0;
        if (totalIncome > 0) return roundNumber(margin / totalIncome, 4);
        if (totalCost > 0) return roundNumber(margin / totalCost, 4);
        return margin < 0 ? -1 : 0;
    }

    calculateTotalCostForSelectedYear(): number {
        const totalCost: number = this.getGraphCostData()
            .slice(0, this.getMonthsToInclude())
            .reduce((total: number, financialData: number) => total + financialData, 0);

        return totalCost;
    }

    calculateTotalIncomeForSelectedYear(): number {
        const totalIncome: number = this.incomeForEmployee.financialData
            .slice(0, this.getMonthsToInclude())
            .reduce((total: number, financialData: number) => total + financialData, 0);

        return totalIncome;
    }

    calculateTotalMarginForSelectedYear(): number {
        const monthsToInclude = this.getMonthsToInclude();
        const totalIncome: number = this.incomeForEmployee.financialData
            .slice(0, monthsToInclude)
            .reduce((total: number, financialData: number) => total + financialData, 0);

        const totalCost: number = this.getGraphCostData()
            .slice(0, monthsToInclude)
            .reduce((total: number, financialData: number) => total + financialData, 0);

        return totalIncome - totalCost;
    }

    displayInfo($event: CalendarClickEventData) {
        const modalRef = this.modalService.open(ChartInformationModalComponent, {
            windowClass: 'modal-prompt modal-prompt--min-content',
            animation: false,
        });
        modalRef.componentInstance.month = $event.month;
        modalRef.componentInstance.year = this.dateNavigationOptions.selectedDate.getFullYear();
        modalRef.componentInstance.dataset = DATASETS.findDataset($event.datasetIndex);
        modalRef.componentInstance.objectId = this.employee.uuid;
        modalRef.componentInstance.title = `${this.employee.firstName} ${this.employee.lastName}`;
        modalRef.componentInstance.chartInformationObjectType = ChartInformationObjectType.EMPLOYEE;
    }

    private fetchData() {
        this.xLabels = this.seedLabels();
        const selectedYear = this.getSelectedYear();

        // Always refetch income for the selected year so newly linked/unlinked invoices
        // are reflected immediately when reopening the employee financial tab.
        this.subscriptions.add(this.store.dispatch(new FetchIncomeForEmployee(this.employee.uuid, selectedYear)).subscribe());

        this.subscriptions.add(
            this.store.select(FinancialState.incomeForEmployee(this.employee.uuid)).subscribe((income) => {
                if (income?.financialData.length !== 12 || income.detailId !== this.employee.uuid) {
                    return;
                }

                this.incomeForEmployee = income;

                if (this.costsForEmployee?.financialData.length === 12) this.createChart();
            }),
        );

        // Freelancers still use backend invoice costs. Payroll graph costs are
        // recalculated from timesheet hours below, matching the default detail
        // modal ("By hours") instead of showing a flat monthly budget line.
        this.subscriptions.add(this.store.dispatch(new FetchHourlyCostsForEmployee(this.employee.uuid, selectedYear)).subscribe());
        this.subscriptions.add(
            this.store.select(FinancialState.costsForEmployee(this.employee.uuid)).subscribe((costs) => {
                if (costs?.financialData.length !== 12 || costs.detailId !== this.employee.uuid) {
                    return;
                }
                this.costsForEmployee = costs;
                if (this.incomeForEmployee?.financialData.length === 12) this.createChart();
            }),
        );

        // For payroll employees, additionally fetch timesheets + budgets so the
        // chart tooltip can show worked hours per project and the hourly rate.
        // These do NOT influence the cost values shown in the chart anymore.
        if (this.employee.type !== 'FREELANCE') {
            this.subscriptions.add(this.store.dispatch(new FetchTimesheetsForEmployee(this.employee.uuid)).subscribe());
            this.subscriptions.add(this.store.dispatch(new FetchEmployeesWithBudgets()).subscribe());

            this.subscriptions.add(
                this.store.select(TimesheetState.timesheets).subscribe((tss) => {
                    this.timesheetsForEmployee = tss || [];
                    this.recomputeTooltipData(this.getSelectedYear());
                }),
            );

            this.subscriptions.add(
                this.store.select(BudgetState.EmployeesWithBudgets).subscribe((employees) => {
                    this.employeesWithBudgets = employees || [];
                    this.recomputeTooltipData(this.getSelectedYear());
                }),
            );
        }
    }

    private createChart() {
        const costData = this.getGraphCostData();
        const margin: number[] = [];
        const marginPercentage: number[] = [];

        for (let i = 0; i < costData.length; i++) {
            margin.push(this.incomeForEmployee.financialData[i] - costData[i]);
            marginPercentage.push((this.incomeForEmployee.financialData[i] - costData[i]) / this.incomeForEmployee.financialData[i]);
        }

        let lastMonth: number = this.getMonthsToInclude();

        this.xLabels = this.seedLabels();
        for (let i = 0; i < this.xLabels.length; i++) {
            if (!isNaN(marginPercentage[i]) && Number.isFinite(marginPercentage[i])) {
                this.xLabels[i] += `: ${roundNumber(marginPercentage[i] * 100)}%`;
            }
        }

        this.data = [costData.slice(0, lastMonth), this.incomeForEmployee.financialData.slice(0, lastMonth), margin.slice(0, lastMonth)];
        this.labels = ['Cost', 'Income', 'Margin'];

        this.options = {
            plugins: {
                tooltip: {
                    enabled: false,
                    position: 'nearest',
                    external: externalTooltipHandler,
                    callbacks: {
                        // Provide custom label lines to include hours per project for the cost dataset
                        label: (context) => {
                            const datasetIndex = context.datasetIndex ?? 0;
                            const dataIndex = context.dataIndex ?? 0;
                            const value = context.parsed?.y ?? 0;
                            const currency = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
                            const currencyWithDecimals = new Intl.NumberFormat('nl-BE', {
                                style: 'currency',
                                currency: 'EUR',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            });

                            if (datasetIndex === 0) {
                                const rate = (this.hourlyRateByMonth && this.hourlyRateByMonth[dataIndex]) || 0;
                                const lines: string[] = [`Cost: ${currency.format(value)}`, `Rate: ${currencyWithDecimals.format(rate)}/h`];
                                const projects = this.projectHoursByMonth.get(dataIndex) || [];
                                projects.forEach((p) => lines.push(`• ${p.project} — ${p.hours}h`));
                                return lines as unknown as string; // external handler will handle arrays
                            }

                            if (datasetIndex === 1) {
                                return `Income: ${currency.format(value)}`;
                            }
                            return `Value: ${currency.format(value)}`;
                        },
                    },
                },
            },
        };
    }

    /**
     * Recompute hourly rate (from budgets) and worked hours per project for
     * graph tooltips. The financial cost line itself must keep using
     * `costsForEmployee.financialData`, which is the shared backend cost source
     * used by the employee overview table.
     */
    private recomputeTooltipData(currentYear: number) {
        if (!this.employee?.uuid || !this.employeesWithBudgets) return;
        const emp = this.employeesWithBudgets.find((e) => e.uuid === this.employee.uuid);
        if (!emp) return;

        const workPct = emp?.workPercentage ?? this.employee?.workPercentage;
        this.projectHoursByMonth.clear();
        this.hourlyRateByMonth = new Array(12).fill(0);

        for (let m = 0; m < 12; m++) {
            const year = currentYear;
            const monthIndex = m;

            // Per-month hourly rate from budgets overview logic
            const rateDate = new Date(year, monthIndex, 15);
            const monthlyTotalForMonth = this.budgetCalc.computeMonthlyBudgetTotal((emp.budgets as Budget[]) || [], rateDate);
            const hourlyRate = this.budgetCalc.computeHourlyBudgetedCost(monthlyTotalForMonth, workPct);
            this.hourlyRateByMonth[m] = hourlyRate;

            const hoursPerProject: Map<string, { name: string; hours: number }> = new Map();
            const monthTimesheets = (this.timesheetsForEmployee || []).filter((ts) => {
                if (!ts?.expirationDate) return false;
                const d = new Date(ts.expirationDate.toString());
                return d.getFullYear() === year && d.getMonth() === monthIndex;
            });

            monthTimesheets.forEach((ts) => {
                (ts.timeslots || []).forEach((slot) => {
                    const projectName = slot?.project?.title || slot?.project?.companyName || 'Unknown project';
                    const key = slot?.project?.uuid || projectName;
                    const minutes = slot?.minutes || 0;
                    const hours = minutes / 60;
                    if (!hoursPerProject.has(key)) {
                        hoursPerProject.set(key, { name: projectName, hours: 0 });
                    }
                    hoursPerProject.get(key).hours += hours;
                });
            });

            const projectHoursList: { project: string; hours: number }[] = [];
            hoursPerProject.forEach((val) => {
                const hours = Math.round(val.hours * 10) / 10;
                projectHoursList.push({ project: val.name, hours });
            });
            this.projectHoursByMonth.set(m, projectHoursList);
        }

        // Refresh the chart so updated tooltip rate/hours take effect.
        if (this.costsForEmployee?.financialData.length === 12 && this.incomeForEmployee?.financialData.length === 12) {
            this.createChart();
        }
    }
}

export class DATASETS {
    static readonly COST = new DATASETS(0);
    static readonly INCOME = new DATASETS(1);

    private constructor(public readonly index: number) {}

    static findDataset(index: number) {
        switch (index) {
            case 0:
                return DATASETS.COST;
            case 1:
                return DATASETS.INCOME;
        }
    }
}
